/**
 * Task Mutations Hook
 *
 * Provides mutation functions for task operations with TanStack Query cache invalidation.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateTask, completeTask, createTask } from '../../api/client';
import type { Task } from '../../types';

export function useTaskMutations() {
  const queryClient = useQueryClient();

  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const completeTaskMutation = useMutation({
    mutationFn: ({ taskId, options }: { taskId: string; options?: { actualMinutes?: number } }) =>
      completeTask(taskId, options),
    onSuccess: invalidateTasks,
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({
      taskId,
      updates,
    }: {
      taskId: string;
      updates: Record<string, unknown>;
    }) => updateTask(taskId, updates as Parameters<typeof updateTask>[1]),
    onSuccess: invalidateTasks,
  });

  const createTaskMutation = useMutation({
    mutationFn: (data: Parameters<typeof createTask>[0]) => createTask(data),
    onSuccess: invalidateTasks,
  });

  const completeTaskAsync = async ({
    taskId,
    options,
  }: {
    taskId: string;
    options?: { actualMinutes?: number };
  }) => completeTaskMutation.mutateAsync({ taskId, options });

  const updateTaskAsync = async ({
    taskId,
    updates,
  }: {
    taskId: string;
    updates: Record<string, unknown>;
  }) => updateTaskMutation.mutateAsync({ taskId, updates });

  const duplicateTaskAsync = async ({ task }: { task: Task }) => {
    const newTask = await createTaskMutation.mutateAsync({
      content: `${task.content} (copy)`,
      description: task.description,
      priority: task.priority,
      dueString: task.due?.date,
      labels: task.labels,
    });
    return newTask;
  };

  const deleteTaskAsync = async ({ taskId }: { taskId: string }) => {
    return completeTaskMutation.mutateAsync({ taskId });
  };

  return {
    completeTaskAsync,
    updateTaskAsync,
    duplicateTaskAsync,
    deleteTaskAsync,
  };
}
